import { useEffect } from 'react';

/**
 * SEO Head Component - Dynamically updates meta tags for SEO
 * Supports: title, description, keywords, canonical, Open Graph, Twitter Cards, JSON-LD Schema
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords = [],
  canonical,
  ogImage = 'https://uplino.in/og-default.jpg',
  ogType = 'website',
  schema = null,
  noIndex = false
}) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper to update or create meta tag
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const updateLink = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic SEO meta tags
    updateMeta('description', description);
    if (keywords.length > 0) {
      updateMeta('keywords', keywords.join(', '));
    }

    // Robots
    if (noIndex) {
      updateMeta('robots', 'noindex, nofollow');
    } else {
      updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Canonical URL
    if (canonical) {
      updateLink('canonical', canonical);
    }

    // Open Graph tags
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:type', ogType, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:site_name', 'uplino.in', true);
    updateMeta('og:locale', 'en_IN', true);
    if (canonical) {
      updateMeta('og:url', canonical, true);
    }

    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImage);

    // JSON-LD Schema
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Schema cleanup handled by React's re-render
    };
  }, [title, description, keywords, canonical, ogImage, ogType, schema, noIndex]);

  return null; // This component doesn't render anything
};

/**
 * Generate LocalBusiness Schema for city/service pages
 */
export const generateLocalBusinessSchema = (city, service, url) => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `${service} in ${city} - uplino.in`,
    "description": `Find the best ${service.toLowerCase()} in ${city}. Verified listings with contact details. Browse ${city} ${service.toLowerCase()} on uplino.in`,
    "url": url,
    "areaServed": {
      "@type": "City",
      "name": city
    },
    "serviceType": service,
    "image": "https://uplino.in/og-default.jpg",
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city,
      "addressCountry": "IN"
    }
  };
};

/**
 * Generate BreadcrumbList Schema
 */
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

/**
 * Generate ItemList Schema for ads listing
 */
export const generateItemListSchema = (ads, city, service) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${service} in ${city}`,
    "description": `List of ${service.toLowerCase()} providers in ${city}`,
    "numberOfItems": ads.length,
    "itemListElement": ads.slice(0, 10).map((ad, index) => {
      const citySlug = ad.city_slug || (ad.is_global_pinned ? 'india' : undefined);
      const categorySlug = ad.category_slug;
      const slug = ad.slug;
      const seoUrl = citySlug && categorySlug && slug
        ? `https://uplino.in/${citySlug}/${categorySlug}/${slug}`
        : `https://uplino.in/ad/${ad.id}`;

      return {
        "@type": "ListItem",
        "position": index + 1,
        "name": ad.title,
        "url": seoUrl
      };
    })
  };
};

/**
 * Generate WebPage Schema
 */
export const generateWebPageSchema = (title, description, url) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": url,
    "isPartOf": {
      "@type": "WebSite",
      "name": "uplino.in",
      "url": "https://uplino.in"
    },
    "inLanguage": "en-IN",
    "dateModified": new Date().toISOString()
  };
};

/**
 * Generate Organization Schema for homepage
 */
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "uplino.in",
    "url": "https://uplino.in",
    "logo": "https://uplino.in/logo.png",
    "description": "India's premier classified ads platform for premium adult services",
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "sameAs": []
  };
};

export default SEOHead;
